import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Download,
  ChevronRight,
  ChevronLeft,
  Users,
  Eye,
  Upload,
  Info,
  RefreshCw,
  Calendar,
  Link2,
  Mail,
  UserX,
  Zap,
  Check,
  X,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/ui/use-toast';
import { DotyposService } from '@/lib/integrations/dotypos-service';
import {
  DotyposEmployee,
  DotyposConfig,
  ImportConflict,
  ImportSummary,
  ImportStep,
  ImportWizardState,
  AttendanceSyncMode,
  CreateShiftsSyncResult,
  MatchExistingSyncResult,
  EmailIssue,
  EmailResolution,
  EmailResolutionType,
  SmartMatch,
  SmartMatchResolution,
} from '@/types/dotypos';
import { cn } from '@/lib/core/utils';
import { useDotyposAttendanceSync } from '@/hooks/integrations/useDotyposAttendanceSync';
import { supabase } from '@/integrations/supabase/client';

interface TimeTrackingEnableWizardProps {
  businessId: string | null;
  businessName?: string;
}

type WizardStep = 'select_source' | 'fetch' | 'review' | 'resolve_emails' | 'import' | 'sync_attendance' | 'complete';

const TimeTrackingEnableWizard: React.FC<TimeTrackingEnableWizardProps> = ({
  businessId,
  businessName
}) => {
  const { setTimeTrackingEnabled, setTimeTrackingDataSource } = useAuth();
  const [selectedDataSource, setSelectedDataSource] = useState<'internal' | 'dotypos' | 'hybrid'>('internal');
  const [currentStep, setCurrentStep] = useState<WizardStep>('select_source');

  // Dotypos import state
  const [wizardState, setWizardState] = useState<ImportWizardState>({
    step: 'fetch',
    fetchedEmployees: [],
    conflicts: [],
    conflictResolutions: new Map(),
    smartMatches: [],
    smartMatchResolutions: new Map(),
    emailIssues: [],
    emailResolutions: new Map(),
    isLoading: false,
    error: null,
  });

  const [config, setConfig] = useState<DotyposConfig | null>(null);
  const [skipDeleted, setSkipDeleted] = useState(true);
  const [skipDisabled, setSkipDisabled] = useState(false);
  const [syncMode, setSyncMode] = useState<AttendanceSyncMode>('create_shifts');
  const [attendanceSyncResult, setAttendanceSyncResult] = useState<
    CreateShiftsSyncResult | MatchExistingSyncResult | null
  >(null);
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set());
  const [existingEmployeesList, setExistingEmployeesList] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [shiftPreview, setShiftPreview] = useState<{
    total_attendance_records: number;
    shifts_to_create: number;
    skipped_no_mapping: number;
    skipped_duplicates?: number;
    needs_review?: number;
    shifts: Array<{
      employeeId: string;
      employeeName: string;
      date: string;
      // Actual times from Dotypos attendance
      arrivalTime: string;
      departureTime: string;
      // Expected times (from matched shift or needs to be set)
      expectedStartTime: string | null;
      expectedEndTime: string | null;
      duration: number;
      needsReview?: boolean;
      matchStatus?: 'match' | 'partial' | 'no_match' | 'duplicate';
      existingShiftId?: string;
      existingShiftTime?: string;
      timeDifferenceMinutes?: number;
      hasExistingTimeLogs?: boolean;
    }>;
    match_summary?: {
      total_with_matches: number;
      total_without_matches: number;
      total_duplicates?: number;
      total_needs_review?: number;
      match_percentage: number;
      recommended_mode: 'create_shifts' | 'match_existing';
    };
  } | null>(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);

  const { isSyncing, syncAttendanceWithShifts } = useDotyposAttendanceSync();

  const handleConfirmDataSource = () => {
    if (selectedDataSource === 'internal') {
      // For internal, enable immediately
      setTimeTrackingDataSource(selectedDataSource);
      setTimeTrackingEnabled(true);

      toast.success("Time tracking enabled", {
        description: "Data source set to: Internal (QR/Manual)"
      });
    } else {
      // For dotypos/hybrid, start the import wizard
      setCurrentStep('fetch');
      loadSavedConfigAndFetch();
    }
  };

  const loadSavedConfigAndFetch = async () => {
    if (!businessId) return;

    setWizardState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const loadedConfig = await DotyposService.loadConfig(businessId);

      if (!loadedConfig) {
        throw new Error('No Dotypos configuration found. Please configure Dotypos connection in Settings first.');
      }

      setConfig(loadedConfig);
      setSkipDeleted(loadedConfig.import_settings?.skipDeletedEmployees ?? true);
      setSkipDisabled(!(loadedConfig.import_settings?.importDisabledEmployees ?? true));

      // Auto-fetch employees
      await fetchEmployeesWithConfig(loadedConfig);
    } catch (error: any) {
      toast.error('Configuration Error', {
        description: error.message || 'Failed to load Dotypos configuration',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const fetchEmployeesWithConfig = async (configToUse: DotyposConfig) => {
    if (!businessId) return;

    try {
      // Fetch existing employees to get their emails for validation and for assimilation
      const { data: existingEmployeesData } = await supabase
        .from('employees')
        .select('id, name, email')
        .eq('business_id', businessId);

      if (existingEmployeesData) {
        const emailSet = new Set(
          existingEmployeesData.map(e => e.email.toLowerCase())
        );
        setExistingEmails(emailSet);
        setExistingEmployeesList(existingEmployeesData);
      }

      // Fetch employees
      const employees = await DotyposService.fetchEmployees({
        client_id: configToUse.client_id,
        client_secret: configToUse.client_secret,
        cloud_id: configToUse.cloud_id,
        business_id: configToUse.business_id,
        refresh_token: configToUse.refresh_token,
      });

      // Filter based on settings
      const filtered = DotyposService.filterEmployees(
        employees,
        skipDeleted,
        skipDisabled
      );

      if (filtered.length === 0) {
        toast.error('No Employees Found', {
          description: 'No employees match the import criteria',
        });
        setWizardState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      // Detect conflicts
      const conflicts = await DotyposService.detectConflicts(businessId, filtered);

      // Detect smart matches (name-based) for ALL non-conflicted employees
      const smartMatches = await DotyposService.detectSmartMatches(businessId, filtered, conflicts);

      // Detect email issues
      const smartMatchedIds = new Set(smartMatches.map(m => m.dotyposEmployee.id));
      const employeesNeedingEmailResolution = filtered.filter(
        emp => !smartMatchedIds.has(emp.id)
      );
      const emailIssues = await DotyposService.detectEmailIssues(businessId, employeesNeedingEmailResolution, conflicts);

      setWizardState({
        ...wizardState,
        step: 'review',
        fetchedEmployees: filtered,
        conflicts,
        smartMatches,
        emailIssues,
        isLoading: false,
      });

      setCurrentStep('review');

      toast.success('Employees Fetched', {
        description: `Found ${filtered.length} employees. ${smartMatches.length} smart match(es), ${emailIssues.length} email issue(s) detected.`,
      });
    } catch (error: any) {
      toast.error('Fetch Failed', {
        description: error.message || 'Failed to fetch employees from Dotypos',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const handleConflictResolution = (dotyposId: number, action: 'skip' | 'update' | 'create') => {
    setWizardState((prev) => {
      const newResolutions = new Map(prev.conflictResolutions);
      newResolutions.set(dotyposId, action);
      return { ...prev, conflictResolutions: newResolutions };
    });
  };

  const handleSmartMatchResolution = (dotyposId: number, resolution: SmartMatchResolution) => {
    setWizardState((prev) => {
      const newResolutions = new Map(prev.smartMatchResolutions);
      newResolutions.set(dotyposId, resolution);
      return { ...prev, smartMatchResolutions: newResolutions };
    });
  };

  const handleEmailResolution = (
    dotyposId: number,
    resolutionType: EmailResolutionType,
    manualEmail?: string,
    linkToEmployeeId?: string
  ) => {
    setWizardState((prev) => {
      const newResolutions = new Map(prev.emailResolutions);
      newResolutions.set(dotyposId, {
        dotyposId,
        resolutionType,
        manualEmail,
        linkToEmployeeId,
      });
      return { ...prev, emailResolutions: newResolutions };
    });
  };

  const getEmailResolution = (dotyposId: number): EmailResolution | undefined => {
    return wizardState.emailResolutions.get(dotyposId);
  };

  const allEmailIssuesResolved = (): boolean => {
    return wizardState.emailIssues.every((issue) => {
      const resolution = wizardState.emailResolutions.get(issue.dotyposEmployee.id);
      if (!resolution) return false;
      if (resolution.resolutionType === 'manual') {
        if (!resolution.manualEmail) return false;
        const error = getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id);
        if (error) return false;
      }
      if (resolution.resolutionType === 'link_existing' && !resolution.linkToEmployeeId) return false;
      if (resolution.resolutionType === 'assimilate' && !resolution.linkToEmployeeId) return false;
      return true;
    });
  };

  const getAvailableEmployeesForAssimilation = (currentDotyposId: number): Array<{ id: string; name: string; email: string }> => {
    const usedEmployeeIds = new Set<string>();

    for (const [dotyposId, resolution] of wizardState.emailResolutions.entries()) {
      if (dotyposId !== currentDotyposId && resolution.linkToEmployeeId) {
        usedEmployeeIds.add(resolution.linkToEmployeeId);
      }
    }

    for (const conflict of wizardState.conflicts) {
      const conflictRes = wizardState.conflictResolutions.get(conflict.dotyposEmployee.id);
      if (conflictRes === 'update') {
        usedEmployeeIds.add(conflict.existingEmployee.id);
      }
    }

    return existingEmployeesList.filter(emp => !usedEmployeeIds.has(emp.id));
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isEmailInTagInn = (email: string): boolean => {
    return existingEmails.has(email.toLowerCase());
  };

  const isEmailUsedInBatch = (email: string, currentDotyposId: number): boolean => {
    const emailLower = email.toLowerCase();

    for (const [dotyposId, resolution] of wizardState.emailResolutions.entries()) {
      if (dotyposId !== currentDotyposId &&
          resolution.resolutionType === 'manual' &&
          resolution.manualEmail?.toLowerCase() === emailLower) {
        return true;
      }
    }

    const employeesWithEmail = wizardState.fetchedEmployees.filter(
      emp => emp.email?.toLowerCase() === emailLower && emp.id !== currentDotyposId
    );

    for (const emp of employeesWithEmail) {
      const resolution = wizardState.emailResolutions.get(emp.id);
      if (!resolution || (resolution.resolutionType !== 'manual' && resolution.resolutionType !== 'skip')) {
        return true;
      }
    }

    return false;
  };

  const getEmailValidationError = (email: string, dotyposId: number): string | null => {
    if (!email) return null;
    if (!isValidEmail(email)) return 'Please enter a valid email address';
    if (isEmailInTagInn(email)) return 'This email already exists in TagInn';
    if (isEmailUsedInBatch(email, dotyposId)) return 'This email is already used by another employee in this import';
    return null;
  };

  const handleImport = async () => {
    if (!businessId) return;

    setCurrentStep('import');
    setWizardState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const summary = await DotyposService.importEmployees(
        businessId,
        wizardState.fetchedEmployees,
        wizardState.conflicts,
        wizardState.conflictResolutions,
        wizardState.smartMatches,
        wizardState.smartMatchResolutions,
        wizardState.emailResolutions,
        undefined
      );

      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        summary,
      }));

      setCurrentStep('sync_attendance');

      toast.success('Import Complete', {
        description: `Imported ${summary.created} new employees, updated ${summary.updated}, skipped ${summary.skipped}. Now syncing attendance...`,
      });
    } catch (error: any) {
      toast.error('Import Failed', {
        description: error.message || 'Failed to import employees',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const handleFetchAttendancePreview = async () => {
    if (!businessId) return;

    setIsFetchingPreview(true);
    setShiftPreview(null);

    try {
      const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';
      const response = await fetch(`${apiBase}/dotypos/preview-attendance-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          sync_mode: 'all',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch attendance preview');
      }

      if (data.success) {
        setShiftPreview(data.preview);

        // Set default sync mode based on recommendation
        if (data.preview.match_summary?.recommended_mode) {
          setSyncMode(data.preview.match_summary.recommended_mode);
        }

        toast.success("Attendance Fetched", {
          description: `Found ${data.preview.shifts_to_create} shifts to create from ${data.preview.total_attendance_records} attendance records`
        });
      } else {
        throw new Error(data.error || 'Preview failed');
      }
    } catch (error: any) {
      toast.error('Preview Failed', {
        description: error.message || 'Failed to fetch attendance preview',
      });
    } finally {
      setIsFetchingPreview(false);
    }
  };

  const handleSyncAttendance = async () => {
    if (!businessId) return;

    setWizardState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await syncAttendanceWithShifts(
        businessId,
        syncMode,
        'all'
      );

      if (result) {
        setAttendanceSyncResult(result);
        // Stay on sync_attendance step to show the results
        setWizardState((prev) => ({
          ...prev,
          isLoading: false,
        }));

        toast.success("Shifts Created", {
          description: `Created ${(result as CreateShiftsSyncResult).shifts_created || 0} shifts from Dotypos attendance`
        });
      } else {
        throw new Error('Attendance sync failed');
      }
    } catch (error: any) {
      toast.error('Attendance Sync Failed', {
        description: error.message || 'Failed to sync attendance data',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const handleCompleteSetup = () => {
    // Enable time tracking when user clicks Continue from sync step
    setTimeTrackingDataSource(selectedDataSource);
    setTimeTrackingEnabled(true);

    const sourceLabels = {
      dotypos: 'Dotypos POS',
      hybrid: 'Hybrid (Both)'
    };

    toast.success("Time tracking enabled", {
      description: `Setup complete. Data source: ${sourceLabels[selectedDataSource as 'dotypos' | 'hybrid']}`
    });

    setCurrentStep('complete');
  };

  const handleBackToSourceSelection = () => {
    setCurrentStep('select_source');
    setWizardState({
      step: 'fetch',
      fetchedEmployees: [],
      conflicts: [],
      conflictResolutions: new Map(),
      smartMatches: [],
      smartMatchResolutions: new Map(),
      emailIssues: [],
      emailResolutions: new Map(),
      isLoading: false,
      error: null,
    });
  };

  // Render step indicator
  const renderStepIndicator = () => {
    const steps = [
      { id: 'select_source', label: 'Source', icon: Eye },
      { id: 'fetch', label: 'Fetch', icon: Download },
      { id: 'review', label: 'Review', icon: Users },
    ];

    if (wizardState.emailIssues.length > 0) {
      steps.push({ id: 'resolve_emails', label: 'Emails', icon: Mail });
    }

    steps.push(
      { id: 'import', label: 'Import', icon: Upload },
      { id: 'sync_attendance', label: 'Sync', icon: RefreshCw },
      { id: 'complete', label: 'Done', icon: CheckCircle2 }
    );

    const currentIndex = steps.findIndex((s) => s.id === currentStep);

    return (
      <div className="flex items-center justify-between mb-6">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors',
                    isActive && 'border-primary bg-primary text-primary-foreground',
                    isCompleted && 'border-green-500 bg-green-500 text-white',
                    !isActive && !isCompleted && 'border-gray-300 bg-white dark:bg-gray-800'
                  )}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <span className={cn('text-xs mt-1', isActive && 'font-semibold')}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-1',
                    isCompleted ? 'bg-green-500' : 'bg-gray-300'
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Render data source selection step
  const renderSelectSourceStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-medium mb-1">Choose Data Source</h3>
        <p className="text-sm text-muted-foreground">
          Select how you want to track employee attendance and working hours.
        </p>
      </div>

      <RadioGroup
        value={selectedDataSource}
        onValueChange={(value) => setSelectedDataSource(value as 'internal' | 'dotypos' | 'hybrid')}
        className="space-y-3"
      >
        <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
          <RadioGroupItem value="internal" id="wizard-internal" className="mt-1" />
          <div className="flex-1">
            <Label htmlFor="wizard-internal" className="font-medium cursor-pointer">
              Internal (QR/Manual)
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Track attendance using QR code scanning or manual time entries within the app.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
          <RadioGroupItem value="dotypos" id="wizard-dotypos" className="mt-1" />
          <div className="flex-1">
            <Label htmlFor="wizard-dotypos" className="font-medium cursor-pointer">
              Dotypos POS
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Import attendance data from Dotypos POS system (requires Dotypos integration setup).
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
          <RadioGroupItem value="hybrid" id="wizard-hybrid" className="mt-1" />
          <div className="flex-1">
            <Label htmlFor="wizard-hybrid" className="font-medium cursor-pointer">
              Hybrid (Both)
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Use both internal tracking and Dotypos import for comprehensive coverage.
            </p>
          </div>
        </div>
      </RadioGroup>

      <div className="flex justify-end pt-2">
        <Button onClick={handleConfirmDataSource}>
          {selectedDataSource === 'dotypos' || selectedDataSource === 'hybrid'
            ? 'Continue to Employee Sync'
            : 'Enable Time Tracking'}
        </Button>
      </div>
    </div>
  );

  // Render fetch step
  const renderFetchStep = () => (
    <div className="space-y-4 flex flex-col items-center justify-center py-8">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
      <h3 className="text-lg font-semibold">Fetching Employees...</h3>
      <p className="text-sm text-muted-foreground text-center">
        Loading employee data from Dotypos
      </p>
      <Progress value={undefined} className="w-full" />

      {wizardState.error && (
        <Alert variant="destructive" className="mt-4">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {wizardState.error}
            <br />
            <span className="text-xs mt-2 inline-block">
              Please configure Dotypos connection in Settings.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {wizardState.error && (
        <Button variant="outline" onClick={handleBackToSourceSelection}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Source Selection
        </Button>
      )}
    </div>
  );

  // Render review step
  const renderReviewStep = () => {
    // Create a map of smart matches by dotypos employee id
    const smartMatchMap = new Map(
      wizardState.smartMatches.map(m => [m.dotyposEmployee.id, m])
    );

    // Create a map of conflicts by dotypos employee id
    const conflictMap = new Map(
      wizardState.conflicts.map(c => [c.dotyposEmployee.id, c])
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Review Import</h3>
            <p className="text-sm text-muted-foreground">
              {wizardState.fetchedEmployees.length} employees ready to import
            </p>
          </div>
          <div className="flex gap-2">
            {wizardState.conflicts.length > 0 && (
              <Badge variant="destructive">
                {wizardState.conflicts.length} conflicts
              </Badge>
            )}
            {wizardState.smartMatches.length > 0 && (
              <Badge>
                {wizardState.smartMatches.length} smart matches
              </Badge>
            )}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-2 gap-4">
          {/* Left column - Fetched Employees */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2 sticky top-0 bg-background py-2">
              <Download className="w-4 h-4 text-primary" />
              Fetched from Dotypos
            </h4>
            <div className="space-y-2">
              {wizardState.fetchedEmployees.map((emp) => {
                const hasConflict = conflictMap.has(emp.id);
                const hasSmartMatch = smartMatchMap.has(emp.id);

                return (
                  <div
                    key={emp.id}
                    className={cn(
                      "p-3 rounded-lg border",
                      hasConflict && "border-orange-300 bg-orange-50 dark:bg-orange-950/20",
                      hasSmartMatch && !hasConflict && "border-blue-300 bg-blue-50 dark:bg-blue-950/20",
                      !hasConflict && !hasSmartMatch && "border-green-300 bg-green-50 dark:bg-green-950/20"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium">{emp.name}</p>
                        <p className="text-xs text-muted-foreground">{emp.email || 'No email'}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="outline" className="text-xs">
                          {emp.enabled ? 'Active' : 'Inactive'}
                        </Badge>
                        {hasConflict && (
                          <Badge variant="destructive" className="text-xs">
                            Conflict
                          </Badge>
                        )}
                        {hasSmartMatch && !hasConflict && (
                          <Badge className="text-xs">
                            Match
                          </Badge>
                        )}
                        {!hasConflict && !hasSmartMatch && (
                          <Badge variant="outline" className="text-xs border-green-500 text-green-700">
                            New
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right column - Smart Matching & Conflicts */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2 sticky top-0 bg-background py-2">
              <Zap className="w-4 h-4 text-blue-500" />
              Matching & Resolution
            </h4>
            <div className="space-y-2">
              {wizardState.fetchedEmployees.map((emp) => {
                const conflict = conflictMap.get(emp.id);
                const smartMatch = smartMatchMap.get(emp.id);

                // Conflict resolution
                if (conflict) {
                  return (
                    <div
                      key={emp.id}
                      className="p-3 rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-950/20"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium">
                            {conflict.conflictReason === 'dotypos_id_match' ? 'Already Imported' : 'Duplicate Email'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Existing: {conflict.existingEmployee.name}
                          </p>
                        </div>
                      </div>
                      <RadioGroup
                        value={
                          wizardState.conflictResolutions.get(conflict.dotyposEmployee.id) ||
                          conflict.suggestedAction
                        }
                        onValueChange={(value) =>
                          handleConflictResolution(
                            conflict.dotyposEmployee.id,
                            value as 'skip' | 'update' | 'create'
                          )
                        }
                        className="space-y-1"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="skip" id={`skip-${conflict.dotyposEmployee.id}`} />
                          <Label htmlFor={`skip-${conflict.dotyposEmployee.id}`} className="font-normal text-xs">
                            Skip
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="update" id={`update-${conflict.dotyposEmployee.id}`} />
                          <Label htmlFor={`update-${conflict.dotyposEmployee.id}`} className="font-normal text-xs">
                            Update existing
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  );
                }

                // Smart match resolution
                if (smartMatch) {
                  const resolution = wizardState.smartMatchResolutions.get(smartMatch.dotyposEmployee.id);
                  return (
                    <div
                      key={emp.id}
                      className="p-3 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-950/20"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium">
                            → {smartMatch.suggestedEmployee.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {smartMatch.suggestedEmployee.email}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs border-blue-500 text-blue-700">
                          {smartMatch.similarity}%
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={resolution === 'accept' ? 'default' : 'outline'}
                          onClick={() => handleSmartMatchResolution(smartMatch.dotyposEmployee.id, 'accept')}
                          className="flex-1 h-7 text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant={resolution === 'reject' ? 'destructive' : 'outline'}
                          onClick={() => handleSmartMatchResolution(smartMatch.dotyposEmployee.id, 'reject')}
                          className="flex-1 h-7 text-xs"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  );
                }

                // New employee (no conflict, no smart match)
                return (
                  <div
                    key={emp.id}
                    className="p-3 rounded-lg border border-green-300 bg-green-50 dark:bg-green-950/20"
                  >
                    <div className="flex items-center justify-center h-full">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm">Will be created as new</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleBackToSourceSelection}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => {
              const unresolvedSmartMatches = wizardState.smartMatches.filter(
                m => !wizardState.smartMatchResolutions.has(m.dotyposEmployee.id)
              );

              if (unresolvedSmartMatches.length > 0) {
                toast.error('Unresolved Matches', {
                  description: `Please accept or reject all ${unresolvedSmartMatches.length} smart match(es)`,
                });
                return;
              }

              const rejectedMatches = wizardState.smartMatches.filter(
                m => wizardState.smartMatchResolutions.get(m.dotyposEmployee.id) === 'reject'
              );

              const totalEmailIssues = wizardState.emailIssues.length +
                rejectedMatches.filter(m => !m.dotyposEmployee.email).length;

              if (totalEmailIssues > 0) {
                const additionalIssues = rejectedMatches
                  .filter(m => !m.dotyposEmployee.email)
                  .map(m => ({
                    dotyposEmployee: m.dotyposEmployee,
                    issueType: 'missing' as const,
                  }));

                setWizardState((prev) => ({
                  ...prev,
                  emailIssues: [...prev.emailIssues, ...additionalIssues],
                }));
                setCurrentStep('resolve_emails');
              } else {
                handleImport();
              }
            }}
          >
            <ChevronRight className="w-4 h-4 mr-2" />
            {wizardState.emailIssues.length > 0 ? 'Resolve Emails' : 'Start Import'}
          </Button>
        </div>
      </div>
    );
  };

  // Render resolve emails step
  const renderResolveEmailsStep = () => {
    const missingEmails = wizardState.emailIssues.filter(i => i.issueType === 'missing');
    const duplicateInTaginn = wizardState.emailIssues.filter(i => i.issueType === 'duplicate_in_taginn');

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Resolve Email Issues</h3>
          <p className="text-sm text-muted-foreground">
            {wizardState.emailIssues.length} employee(s) need email resolution
          </p>
        </div>

        {missingEmails.map((issue) => {
          const resolution = getEmailResolution(issue.dotyposEmployee.id);
          return (
            <Card key={issue.dotyposEmployee.id} className="border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{issue.dotyposEmployee.name}</CardTitle>
                <CardDescription>No email in Dotypos</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <RadioGroup
                  value={resolution?.resolutionType || ''}
                  onValueChange={(value) =>
                    handleEmailResolution(issue.dotyposEmployee.id, value as EmailResolutionType)
                  }
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="manual" id={`manual-${issue.dotyposEmployee.id}`} />
                    <Label htmlFor={`manual-${issue.dotyposEmployee.id}`} className="font-normal">
                      Enter email manually
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="assimilate" id={`assimilate-${issue.dotyposEmployee.id}`} />
                    <Label htmlFor={`assimilate-${issue.dotyposEmployee.id}`} className="font-normal">
                      Link to existing employee
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="generate_placeholder" id={`placeholder-${issue.dotyposEmployee.id}`} />
                    <Label htmlFor={`placeholder-${issue.dotyposEmployee.id}`} className="font-normal text-muted-foreground">
                      Generate placeholder (no portal access)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="skip" id={`skip-${issue.dotyposEmployee.id}`} />
                    <Label htmlFor={`skip-${issue.dotyposEmployee.id}`} className="font-normal">
                      Skip this employee
                    </Label>
                  </div>
                </RadioGroup>

                {resolution?.resolutionType === 'manual' && (
                  <div className="pl-6">
                    <Input
                      type="email"
                      placeholder="employee@example.com"
                      value={resolution.manualEmail || ''}
                      onChange={(e) =>
                        handleEmailResolution(issue.dotyposEmployee.id, 'manual', e.target.value)
                      }
                      className={cn(
                        resolution.manualEmail && getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id) && 'border-red-500'
                      )}
                    />
                    {resolution.manualEmail && getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id) && (
                      <p className="text-xs text-red-500 mt-1">
                        {getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id)}
                      </p>
                    )}
                  </div>
                )}

                {resolution?.resolutionType === 'assimilate' && (
                  <div className="pl-6">
                    <Select
                      value={resolution.linkToEmployeeId || ''}
                      onValueChange={(value) =>
                        handleEmailResolution(issue.dotyposEmployee.id, 'assimilate', undefined, value)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select existing employee..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableEmployeesForAssimilation(issue.dotyposEmployee.id).map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} ({emp.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Action buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => setCurrentStep('review')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleImport} disabled={!allEmailIssuesResolved()}>
            <Upload className="w-4 h-4 mr-2" />
            Start Import
          </Button>
        </div>
      </div>
    );
  };

  // Render import step
  const renderImportStep = () => (
    <div className="space-y-4 flex flex-col items-center justify-center py-8">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
      <h3 className="text-lg font-semibold">Importing Employees...</h3>
      <p className="text-sm text-muted-foreground">
        Please wait while we import your employees from Dotypos
      </p>
      <Progress value={undefined} className="w-full" />
    </div>
  );

  // Render sync attendance step
  const renderSyncAttendanceStep = () => {
    const hasFetchedAttendance = attendanceSyncResult && attendanceSyncResult.total_fetched > 0;
    const hasPreview = shiftPreview !== null;

    // Format time for display
    const formatTime = (isoString: string) => {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Sync Attendance Data</h3>
            <p className="text-sm text-muted-foreground">
              Import attendance records from Dotypos
            </p>
          </div>
          {hasPreview && !hasFetchedAttendance && (
            <Badge>
              {shiftPreview.shifts_to_create} shifts to create
            </Badge>
          )}
        </div>

        {/* Initial state - fetch preview */}
        {!isFetchingPreview && !hasPreview && !isSyncing && !hasFetchedAttendance && (
          <div className="space-y-4">
            <div className="flex justify-center py-8">
              <Button onClick={handleFetchAttendancePreview} size="lg">
                <Download className="w-4 h-4 mr-2" />
                Fetch Attendance Data
              </Button>
            </div>
          </div>
        )}

        {/* Loading preview */}
        {isFetchingPreview && (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <h4 className="font-medium mb-1">Fetching Attendance Data...</h4>
            <p className="text-sm text-muted-foreground">
              Loading attendance records from Dotypos
            </p>
          </div>
        )}

        {/* Preview loaded - show shifts to create */}
        {hasPreview && !hasFetchedAttendance && !isSyncing && (
          <div className="space-y-4">
            {/* Match detection recommendation */}
            {shiftPreview.match_summary && (
              <Alert className={cn(
                shiftPreview.match_summary.recommended_mode === 'match_existing'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-green-500 bg-green-50 dark:bg-green-950/20'
              )}>
                {shiftPreview.match_summary.recommended_mode === 'match_existing' ? (
                  <Link2 className="h-4 w-4 text-blue-600" />
                ) : (
                  <Calendar className="h-4 w-4 text-green-600" />
                )}
                <AlertDescription>
                  <strong>Recommendation:</strong>{' '}
                  {shiftPreview.match_summary.recommended_mode === 'match_existing' ? (
                    <>
                      Match to existing shifts ({shiftPreview.match_summary.match_percentage}% of attendance records match existing shifts)
                    </>
                  ) : (
                    <>
                      Create new shifts ({shiftPreview.match_summary.total_without_matches} of {shiftPreview.shifts_to_create} records have no matching shifts)
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-5 gap-3">
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xl font-bold text-primary">{shiftPreview.total_attendance_records}</p>
                    <p className="text-xs text-muted-foreground">Records</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">{shiftPreview.match_summary?.total_with_matches || 0}</p>
                    <p className="text-xs text-muted-foreground">Matched</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xl font-bold text-blue-600">{shiftPreview.match_summary?.total_without_matches || shiftPreview.shifts_to_create}</p>
                    <p className="text-xs text-muted-foreground">New</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xl font-bold text-muted-foreground">{shiftPreview.skipped_duplicates || 0}</p>
                    <p className="text-xs text-muted-foreground">Duplicates</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="text-center">
                    <p className="text-xl font-bold text-orange-600">{shiftPreview.skipped_no_mapping}</p>
                    <p className="text-xs text-muted-foreground">Skipped</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Needs review warning */}
            {shiftPreview.needs_review && shiftPreview.needs_review > 0 && (
              <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-sm">
                  <strong>{shiftPreview.needs_review} records</strong> have no matching shifts.
                  New shifts will be created with start/end times equal to arrival/departure times.
                  You can adjust expected times later in the Shift Calendar if needed to track lateness.
                </AlertDescription>
              </Alert>
            )}

            {/* Mode selection */}
            {shiftPreview.match_summary && shiftPreview.match_summary.total_with_matches > 0 && (
              <div className="p-3 rounded-lg border bg-muted/20">
                <RadioGroup
                  value={syncMode}
                  onValueChange={(value) => setSyncMode(value as AttendanceSyncMode)}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="create_shifts" id="mode_create" />
                    <Label htmlFor="mode_create" className="flex-1 cursor-pointer">
                      <span className="font-medium">Create new shifts</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({shiftPreview.shifts_to_create} shifts)
                      </span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="match_existing" id="mode_match" />
                    <Label htmlFor="mode_match" className="flex-1 cursor-pointer">
                      <span className="font-medium">Match to existing shifts</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({shiftPreview.match_summary.total_with_matches} matches)
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Shifts list */}
            {shiftPreview.shifts.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  Attendance Data
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[200px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Employee</th>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Time</th>
                          <th className="text-left p-2 font-medium">Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shiftPreview.shifts.map((shift, index) => (
                          <tr key={index} className="border-t hover:bg-muted/50">
                            <td className="p-2">{shift.employeeName}</td>
                            <td className="p-2">{formatDate(shift.date)}</td>
                            <td className="p-2">
                              {formatTime(shift.actualArrivalTime)} - {formatTime(shift.actualDepartureTime)}
                            </td>
                            <td className="p-2">
                              {shift.matchStatus === 'duplicate' && (
                                <Badge className="bg-gray-200 dark:bg-gray-800 text-xs">
                                  <X className="w-3 h-3 mr-1" />
                                  Duplicate
                                </Badge>
                              )}
                              {shift.matchStatus === 'match' && (
                                <Badge variant="default" className="bg-green-600 text-xs">
                                  <Check className="w-3 h-3 mr-1" />
                                  {shift.existingShiftTime}
                                </Badge>
                              )}
                              {shift.matchStatus === 'partial' && (
                                <Badge className="text-xs">
                                  ~{shift.timeDifferenceMinutes}min
                                </Badge>
                              )}
                              {shift.matchStatus === 'no_match' && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">
                                  {shift.hasExistingTimeLogs ? 'Has logs' : 'New'}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {shiftPreview.shifts.length === 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No shifts can be created from the attendance data. This might be because there are no valid check-in/check-out pairs.
                </AlertDescription>
              </Alert>
            )}

            {/* Action buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={() => setShiftPreview(null)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSyncAttendance}
                disabled={shiftPreview.shifts.length === 0}
              >
                {syncMode === 'match_existing' ? (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    Match {shiftPreview.match_summary?.total_with_matches || 0} to Existing Shifts
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Create {shiftPreview.shifts_to_create} Shifts
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Creating shifts */}
        {isSyncing && (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <h4 className="font-medium mb-1">Creating Shifts...</h4>
            <p className="text-sm text-muted-foreground">
              Please wait while we create shifts from attendance records
            </p>
          </div>
        )}

        {hasFetchedAttendance && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">{attendanceSyncResult.total_fetched}</p>
                    <p className="text-xs text-muted-foreground">Records Fetched</p>
                  </div>
                </CardContent>
              </Card>

              {'shifts_created' in attendanceSyncResult ? (
                <>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{attendanceSyncResult.shifts_created}</p>
                        <p className="text-xs text-muted-foreground">Shifts Created</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{attendanceSyncResult.time_logs_created}</p>
                        <p className="text-xs text-muted-foreground">Time Logs</p>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{attendanceSyncResult.matched_to_shifts}</p>
                        <p className="text-xs text-muted-foreground">Matched to Shifts</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-orange-600">{attendanceSyncResult.unmatched}</p>
                        <p className="text-xs text-muted-foreground">Unmatched</p>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {attendanceSyncResult.errors > 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  {attendanceSyncResult.errors} error(s) occurred during sync.
                </AlertDescription>
              </Alert>
            )}

            {'skipped_no_mapping' in attendanceSyncResult && attendanceSyncResult.skipped_no_mapping > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {attendanceSyncResult.skipped_no_mapping} attendance records were skipped because the employees
                  are not imported into TagInn.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleCompleteSetup}>
                <ChevronRight className="w-4 h-4 mr-2" />
                Complete Setup
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render complete step
  const renderCompleteStep = () => {
    if (!wizardState.summary) return null;

    const { summary } = wizardState;

    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-4">
          <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Setup Complete!</h3>
          <p className="text-sm text-muted-foreground text-center">
            Time tracking is now enabled with Dotypos.
          </p>
        </div>

        <Separator />

        <div className="grid grid-cols-4 gap-2">
          <div className="text-center p-2 bg-green-50 dark:bg-green-950/20 rounded">
            <p className="text-lg font-bold text-green-600">{summary.created}</p>
            <p className="text-xs text-muted-foreground">Created</p>
          </div>
          <div className="text-center p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
            <p className="text-lg font-bold text-blue-600">{summary.updated}</p>
            <p className="text-xs text-muted-foreground">Updated</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-gray-950/20 rounded">
            <p className="text-lg font-bold text-gray-600">{summary.skipped}</p>
            <p className="text-xs text-muted-foreground">Skipped</p>
          </div>
          <div className="text-center p-2 bg-red-50 dark:bg-red-950/20 rounded">
            <p className="text-lg font-bold text-red-600">{summary.failed}</p>
            <p className="text-xs text-muted-foreground">Failed</p>
          </div>
        </div>

        {attendanceSyncResult && (
          <>
            <Separator />
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 bg-primary/10 rounded">
                <p className="text-lg font-bold text-primary">{attendanceSyncResult.total_fetched}</p>
                <p className="text-xs text-muted-foreground">Fetched</p>
              </div>
              {'shifts_created' in attendanceSyncResult ? (
                <>
                  <div className="text-center p-2 bg-green-50 dark:bg-green-950/20 rounded">
                    <p className="text-lg font-bold text-green-600">{attendanceSyncResult.shifts_created}</p>
                    <p className="text-xs text-muted-foreground">Shifts</p>
                  </div>
                  <div className="text-center p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
                    <p className="text-lg font-bold text-blue-600">{attendanceSyncResult.time_logs_created}</p>
                    <p className="text-xs text-muted-foreground">Logs</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center p-2 bg-green-50 dark:bg-green-950/20 rounded">
                    <p className="text-lg font-bold text-green-600">{attendanceSyncResult.matched_to_shifts}</p>
                    <p className="text-xs text-muted-foreground">Matched</p>
                  </div>
                  <div className="text-center p-2 bg-orange-50 dark:bg-orange-950/20 rounded">
                    <p className="text-lg font-bold text-orange-600">{attendanceSyncResult.unmatched}</p>
                    <p className="text-xs text-muted-foreground">Unmatched</p>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Main render
  if (currentStep === 'select_source') {
    return (
      <div className="w-full max-w-2xl mx-auto">
        {renderSelectSourceStep()}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {renderStepIndicator()}

      <div className="w-full">
        {currentStep === 'fetch' && renderFetchStep()}
        {currentStep === 'review' && renderReviewStep()}
        {currentStep === 'resolve_emails' && renderResolveEmailsStep()}
        {currentStep === 'import' && renderImportStep()}
        {currentStep === 'sync_attendance' && renderSyncAttendanceStep()}
        {currentStep === 'complete' && renderCompleteStep()}
      </div>
    </div>
  );
};

export default TimeTrackingEnableWizard;
